import { useMutation } from "@apollo/client";
import { useRouter } from "next/router";
import { toast } from "sonner";

import { UseDisclosureReturn } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import DeleteModal from "@/components/shared/DeleteModal";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import legacyErrorHandler, { assertHasNoUserErrors } from "@/utils/legacyErrorHandler";
import { applicationsPath } from "@/utils/pathGenerators";

const MDeleteApplication_Delete = graphql(`
  mutation MDeleteApplication_Delete($input: AppsDeleteInput!) {
    appsDelete(input: $input) {
      userErrors {
        ... on ArgumentError {
          message
          argument
          attribute
        }
      }
    }
  }
`);

const DeleteApplication_Application = graphql(`
  fragment DeleteApplication_Application on App {
    id
    databaseId
    name
  }
`);

interface Props {
  application: FragmentType<typeof DeleteApplication_Application>;
}

function DeleteApplication({
  application: propApplication,
  ...disclosureProps
}: Props & UseDisclosureReturn) {
  const application = getFragmentData(DeleteApplication_Application, propApplication);
  const { workspaceId } = useReadySearchParams();
  const { push } = useRouter();

  const { onClose } = disclosureProps;

  const [deleteApplicationMutation] = useMutation(MDeleteApplication_Delete);

  const deleteApplication = async () => {
    if (workspaceId && application) {
      try {
        const { data } = await deleteApplicationMutation({
          variables: { input: { appId: application.databaseId } },
          update: cache => {
            const normalizedId = cache.identify(application);
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });

        assertHasNoUserErrors(data?.appsDelete?.userErrors);
        toast.success(`Application '${application.name}' deleted`);
        await push({ pathname: applicationsPath({ workspaceId }) });
        onClose();
      } catch (error) {
        legacyErrorHandler(error);
      }
    }
  };

  return <DeleteModal deleteCallback={deleteApplication} resource={application} {...disclosureProps} />;
}

export default DeleteApplication;
